import { create } from 'zustand';
import {
  ticketService,
  type TicketFilters,
  type CreateTicketDto,
} from '@services/index';
import type { Ticket } from '@centraoneplus/shared-types';
import { queryClient } from '@/lib/queryClient';
import { analyticsKeys } from '@hooks/useAnalytics';

interface TicketsListState {
  // Data
  tickets: Ticket[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;

  // Filters
  filters: TicketFilters;

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;

  // Actions
  fetchTickets: (resetPage?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  /**
   * Fetch EVERY ticket matching the current filters (all pages), not just the
   * page in view — used for CSV export. Paginates in fixed-size batches and
   * returns the flattened list without touching the visible list state.
   */
  fetchAllForExport: () => Promise<Ticket[]>;
  setPage: (page: number) => Promise<void>;
  setLimit: (limit: number) => Promise<void>;
  setFilters: (filters: Partial<TicketFilters>) => void;
  setSorting: (field: string) => void;
  resetFilters: () => void;
  createTicket: (dto: CreateTicketDto) => Promise<Ticket | null>;
  deleteTicket: (id: string) => Promise<boolean>;
  updateTicketInList: (id: string, updates: Partial<Ticket>) => void;
  removeTicketFromList: (id: string) => void;
  reset: () => void;
}

const DEFAULT_FILTERS: TicketFilters = {
  page: 1,
  limit: 20,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

// Fetch a page of tickets, clamping to the last valid page if the requested page
// overshoots the result set. Stale pagination state (e.g. the user was on page 2,
// then tickets were resolved/closed/filtered down to a single page) would otherwise
// request a page beyond the end and the server returns data=[] while total>0 —
// leaving the user staring at an empty list even though tickets exist. When that
// happens we transparently refetch the last page that actually has rows.
async function fetchTicketsPage(
  filters: TicketFilters,
  page: number,
  limit: number,
): Promise<{ data: Ticket[]; total: number; page: number; limit: number }> {
  const response = await ticketService.getTickets({ ...filters, page, limit });

  if (response.data.length === 0 && response.total > 0 && page > 1) {
    const lastPage = Math.max(1, Math.ceil(response.total / (response.limit || limit)));
    if (lastPage < page) {
      return ticketService.getTickets({ ...filters, page: lastPage, limit });
    }
  }

  return response;
}

export const useTicketsListStore = create<TicketsListState>()((set, get) => ({
  // Initial state
  tickets: [],
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  hasMore: false,
  filters: DEFAULT_FILTERS,
  isLoading: false,
  isCreating: false,
  error: null,

  // Fetch tickets with current filters
  fetchTickets: async (resetPage = true) => {
    const { filters, limit } = get();
    const page = resetPage ? 1 : get().page;

    set({ isLoading: true, error: null });

    try {
      const response = await fetchTicketsPage(filters, page, limit);

      set({
        tickets: response.data,
        total: response.total,
        page: response.page,
        totalPages: Math.ceil(response.total / response.limit),
        hasMore: response.page * response.limit < response.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch tickets',
        isLoading: false,
      });
    }
  },

  // Load more tickets (pagination)
  loadMore: async () => {
    const { page, hasMore, isLoading, filters, limit, tickets } = get();
    if (!hasMore || isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const response = await ticketService.getTickets({
        ...filters,
        page: page + 1,
        limit,
      });

      set({
        tickets: [...tickets, ...response.data],
        total: response.total,
        page: response.page,
        totalPages: Math.ceil(response.total / response.limit),
        hasMore: response.page * response.limit < response.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load more tickets',
        isLoading: false,
      });
    }
  },

  // Fetch all matching tickets across every page (for CSV export). Uses the
  // current filters — the same ones driving the visible list — so the export
  // reflects exactly what the user has filtered to, not just the current page.
  fetchAllForExport: async () => {
    const { filters } = get();
    const PAGE_SIZE = 100;

    const first = await ticketService.getTickets({ ...filters, page: 1, limit: PAGE_SIZE });
    const totalPages = Math.ceil(first.total / (first.limit || PAGE_SIZE));
    if (totalPages <= 1) return first.data;

    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        ticketService.getTickets({ ...filters, page: i + 2, limit: PAGE_SIZE }),
      ),
    );

    return [...first.data, ...rest.flatMap((r) => r.data)];
  },

  // Set page for pagination
  setPage: async (page) => {
    const { filters, limit } = get();
    set({ isLoading: true, error: null });

    try {
      const response = await fetchTicketsPage(filters, page, limit);

      set({
        tickets: response.data,
        total: response.total,
        page: response.page,
        totalPages: Math.ceil(response.total / response.limit),
        hasMore: response.page * response.limit < response.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load tickets',
        isLoading: false,
      });
    }
  },

  // Set page size
  setLimit: async (limit) => {
    const { filters } = get();
    set({ limit, isLoading: true, error: null });

    try {
      const response = await ticketService.getTickets({
        ...filters,
        page: 1,
        limit,
      });

      set({
        tickets: response.data,
        total: response.total,
        page: 1,
        totalPages: Math.ceil(response.total / limit),
        hasMore: limit < response.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load tickets',
        isLoading: false,
      });
    }
  },

  // Set filters
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  // Set sorting — toggles asc/desc if same field, defaults to desc for new field
  setSorting: (field) => {
    const { filters } = get();
    const newOrder =
      filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    set((state) => ({
      filters: { ...state.filters, sortBy: field, sortOrder: newOrder },
    }));
    get().fetchTickets();
  },

  // Reset filters
  resetFilters: () => {
    set({ filters: DEFAULT_FILTERS });
  },

  // Create a new ticket
  createTicket: async (dto) => {
    set({ isCreating: true, error: null });

    try {
      const ticket = await ticketService.createTicket(dto);

      // Add to the beginning of the list
      set((state) => ({
        tickets: [ticket, ...state.tickets],
        total: state.total + 1,
        isCreating: false,
      }));

      // A new ticket changes dashboard counts/distributions. Mark the cached
      // analytics queries stale so the Reports page reflects it on next view
      // instead of waiting out the 5-minute React Query staleTime.
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });

      return ticket;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create ticket',
        isCreating: false,
      });
      return null;
    }
  },

  // Delete a ticket
  deleteTicket: async (id) => {
    try {
      await ticketService.deleteTicket(id);
      get().removeTicketFromList(id);
      return true;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete ticket',
      });
      return false;
    }
  },

  // Update a ticket in the list
  updateTicketInList: (id, updates) => {
    set((state) => ({
      tickets: state.tickets.map((ticket) =>
        ticket.id === id ? { ...ticket, ...updates } : ticket
      ),
    }));
  },

  // Remove a ticket from the list
  removeTicketFromList: (id) => {
    set((state) => ({
      tickets: state.tickets.filter((ticket) => ticket.id !== id),
      total: state.total - 1,
    }));
  },

  // Reset state
  reset: () => {
    set({
      tickets: [],
      total: 0,
      page: 1,
      totalPages: 0,
      hasMore: false,
      filters: DEFAULT_FILTERS,
      isLoading: false,
      isCreating: false,
      error: null,
    });
  },
}));
